/**
 * `useFixedShifts` + the fixed-shift mutation hooks — TanStack Query
 * hooks for the cycle-scoped fixed-shift list and its propose / approve /
 * author / remove actions (#687 WS2, PR-6a list + PR-6b mutations).
 *
 * Backed by the routes shipped in PR-5a
 * (`backend/src/api/routes/fixed_shifts.py`):
 *
 *   * `GET    /api/cycles/{cycle_id}/fixed-shifts`       — list fixed
 *     shifts for a cycle. A *lead* caller sees every member's rows (the
 *     fixed-roster view's data); a *member* sees only their own-row rows.
 *     `?own=true` (#2181) returns the caller's own row whatever their role.
 *     No member surface reads this list: the member's Fixed shifts screen
 *     and the Tasks count read `useMeFixedShifts` (`/account/me/fixed-
 *     shifts`, own rows by construction), because a lead opening their own
 *     Fixed shifts screen got the whole team from this one.
 *   * `POST   /api/cycles/{cycle_id}/fixed-shifts`       — author a fixed
 *     shift. A *lead* author creates an `approved` row directly (decision
 *     #4); a member would create a `proposed` row (the member view is
 *     PR-7). The body's `status` is server-controlled — never sent.
 *   * `PATCH  /api/cycles/{cycle_id}/fixed-shifts/{id}`  — the lead's
 *     approve / reject decision on a `proposed` row.
 *   * `DELETE /api/cycles/{cycle_id}/fixed-shifts/{id}`  — the lead
 *     removes any row (a member removes their own `proposed` row).
 *
 * Cache-key shape mirrors `useTeamDateRequests`
 * (`["cycle-fixed-shifts", cycleId, accountId]`). The `accountId`
 * segment keeps switches between dev accounts clean. Every mutation
 * invalidates the `["cycle-fixed-shifts", cycleId]` prefix (the shape
 * `CYCLE_FIXED_SHIFTS_KEY` returns) so the grid re-paints from the
 * server's truth after an author / decision / removal — invalidate-on-
 * success, matching the `useTeamDateRequests` sibling.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { invalidateRosterReads } from "./rosterInvalidation";
import type { components } from "./schema.gen";

export type FixedShiftRead = components["schemas"]["FixedShiftReadResponse"];
export type FixedShiftListResponse = components["schemas"]["FixedShiftListResponse"];
export type FixedShiftCreateBody = components["schemas"]["FixedShiftCreateBody"];
export type FixedShiftDecisionBody = components["schemas"]["FixedShiftDecisionBody"];

const STALE_TIME_MS = 30_000;

export const CYCLE_FIXED_SHIFTS_KEY = (cycleId: string) => ["cycle-fixed-shifts", cycleId] as const;

/**
 * List a cycle's fixed shifts — the LEAD roster's read. Lead callers
 * receive every member's rows; member callers receive their own-row rows
 * only (the backend scopes by role). A member surface never reads this —
 * it uses `useMeFixedShifts`, own rows for every role. The hook stays
 * disabled until both the cycle id and an account id are resolved.
 */
export function useFixedShifts(cycleId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: [...CYCLE_FIXED_SHIFTS_KEY(cycleId ?? ""), accountId ?? ""],
    enabled: Boolean(cycleId && accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<FixedShiftListResponse>(`/api/cycles/${cycleId}/fixed-shifts`),
  });
}

/**
 * Author a fixed shift on a member's cell. The lead caller's POST is
 * born `approved` server-side (direct lead authoring, decision #4); the
 * body carries only `membership_id` / `date` / `role` / optional `note`
 * — the `status` is never trusted from the client. Invalidates the
 * cycle's fixed-shift list on success so the new cell paints in.
 */
export function useCreateFixedShift(cycleId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<FixedShiftRead, ApiError | Error, FixedShiftCreateBody>({
    mutationFn: (body) =>
      apiFetch<FixedShiftRead>(`/api/cycles/${cycleId}/fixed-shifts`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CYCLE_FIXED_SHIFTS_KEY(cycleId ?? "") });
      // #2173 — an approved pin fills its seat on the grid and in the day
      // verdict at once.
      void invalidateRosterReads(queryClient, cycleId);
    },
  });
}

/**
 * The lead's approve / reject decision on a `proposed` fixed shift —
 * `PATCH .../fixed-shifts/{id}` with `{ status: 'approved' | 'rejected' }`.
 * Takes the row id and decision body together so one hook covers both
 * affordances on the grid. Invalidates the cycle's list on success.
 */
export function useDecideFixedShift(cycleId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    FixedShiftRead,
    ApiError | Error,
    { fixedShiftId: string; body: FixedShiftDecisionBody }
  >({
    mutationFn: ({ fixedShiftId, body }) =>
      apiFetch<FixedShiftRead>(`/api/cycles/${cycleId}/fixed-shifts/${fixedShiftId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CYCLE_FIXED_SHIFTS_KEY(cycleId ?? "") });
      // #2173 — an approved pin fills its seat on the grid and in the day
      // verdict at once.
      void invalidateRosterReads(queryClient, cycleId);
    },
  });
}

/**
 * Remove a fixed shift — `DELETE .../fixed-shifts/{id}`. The lead may
 * remove any row in the cycle (an approved fixture or a pending
 * proposal). Invalidates the cycle's list on success so the cell clears.
 */
export function useDeleteFixedShift(cycleId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (fixedShiftId) =>
      apiFetch<void>(`/api/cycles/${cycleId}/fixed-shifts/${fixedShiftId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CYCLE_FIXED_SHIFTS_KEY(cycleId ?? "") });
      // #2173 — an approved pin fills its seat on the grid and in the day
      // verdict at once.
      void invalidateRosterReads(queryClient, cycleId);
    },
  });
}
